import type { ComponentType } from 'react'
import { TYPE } from 'baseui/select'
import { StatefulTooltip } from 'baseui/tooltip'
import BaseSDCheckbox from '@/shared/components/SDComponentsNewDesign/SDCheckbox/SDCheckbox'
import BaseSDDatePicker from '@/shared/components/SDComponentsNewDesign/SDDatePicker/SDDatePicker'
import BaseSDSelect from '@/shared/components/SDComponentsNewDesign/SDSelect/SDSelect'
import {
  PulseFieldContainer,
  PulseFieldLabel,
} from '@/shared/components/SDComponentsNewDesign/SDFieldLabel/styled-components'
import IconFunnel from '@/shared/icons/NewDesign/IconFunnel'
import IconRefresh from '@/shared/icons/NewDesign/IconRefresh'
import { DashboardV2Filters } from '../../types'
import ChoiceGroup from './ChoiceGroup/ChoiceGroup'
import StatusOption from './StatusOption/StatusOption'
import useFiltersPanel from './useFiltersPanel'
import { CardHeader, CardTitle, CardTitleIcon, CountChip, GhostIconButton, OverviewCard } from '../styled-components'
import { DateFieldLabel, FiltersDivider, FiltersStack, OptionList } from './styled-components'

const SDCheckbox = BaseSDCheckbox as ComponentType<any>
const SDDatePicker = BaseSDDatePicker as ComponentType<any>
const SDSelect = BaseSDSelect as ComponentType<any>

const TITLE_ID = 'overview-filters-title'

interface FiltersPanelProps {
  filters: DashboardV2Filters
  applicationType: string
  providersList: Record<string, unknown>
  usersList: Record<string, unknown>
  activeCount: number
  onReset: () => void
}

const FiltersPanel = (props: FiltersPanelProps) => {
  const {
    title,
    activeCountLabel,
    hasActiveFilters,
    resetLabel,
    onReset,
    tooltipOverrides,
    mySubscriptions,
    statusLabel,
    statusOptions,
    choiceGroups,
    provider,
    user,
    selectPlaceholder,
    creationDates,
    datePlaceholder,
  } = useFiltersPanel(props)

  return (
    <OverviewCard aria-labelledby={TITLE_ID}>
      <CardHeader>
        <CardTitle id={TITLE_ID}>
          <CardTitleIcon>
            <IconFunnel size={18} />
          </CardTitleIcon>
          {title}
        </CardTitle>
        <CountChip $hidden={!hasActiveFilters}>{activeCountLabel}</CountChip>
        <StatefulTooltip content={resetLabel} overrides={tooltipOverrides} placement='top' showArrow>
          <GhostIconButton type='button' disabled={!hasActiveFilters} onClick={onReset} aria-label={resetLabel}>
            <IconRefresh size={16} />
          </GhostIconButton>
        </StatefulTooltip>
      </CardHeader>

      <FiltersStack>
        <PulseFieldContainer>
          <PulseFieldLabel>{mySubscriptions.groupLabel}</PulseFieldLabel>
          <SDCheckbox
            checked={mySubscriptions.checked}
            onChange={mySubscriptions.onChange}
            label={mySubscriptions.label}
          />
        </PulseFieldContainer>

        <FiltersDivider />

        <PulseFieldContainer role='group' aria-label={statusLabel}>
          <PulseFieldLabel aria-hidden='true'>{statusLabel}</PulseFieldLabel>
          <OptionList>
            {statusOptions.map(option => (
              <StatusOption key={option.label} {...option} />
            ))}
          </OptionList>
        </PulseFieldContainer>

        <FiltersDivider />

        {choiceGroups.map(group => (
          <ChoiceGroup key={group.key} label={group.label} options={group.options} />
        ))}

        <FiltersDivider />

        <SDSelect
          label={provider.label}
          aria-label={provider.label}
          optionsList={provider.options}
          selectedValue={provider.selectedValue}
          onChangeHandler={provider.onChange}
          placeholder={selectPlaceholder}
          type={TYPE.search}
          clearable
        />
        <SDSelect
          label={user.label}
          aria-label={user.label}
          optionsList={user.options}
          selectedValue={user.selectedValue}
          onChangeHandler={user.onChange}
          placeholder={selectPlaceholder}
          type={TYPE.search}
          clearable
        />
        {creationDates.map(field => (
          <PulseFieldContainer key={field.key}>
            <DateFieldLabel htmlFor={field.inputId}>{field.label}</DateFieldLabel>
            <SDDatePicker
              id={field.inputId}
              formatString='dd/MM/yyyy'
              placeholder={datePlaceholder}
              value={field.value}
              onChange={field.onChange}
              clearable
            />
          </PulseFieldContainer>
        ))}
      </FiltersStack>
    </OverviewCard>
  )
}

export default FiltersPanel
